import React, { useState } from 'react'
import { Spin, Modal, Button, Input, Select, Table, message, notification } from 'antd'
import { useSelector } from 'react-redux'
import CommonTable from '../../components/CommonTable'
import databaseService from '../../api/databaseService'
import dayjs from 'dayjs'

const DatabaseDetails = () => {
  const { databaseInfo, isLoading } = useSelector((state) => state.database)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [selectedDatabase, setSelectedDatabase] = useState('')
  const [selectedServer, setSelectedServer] = useState('')
  const [objectType, setObjectType] = useState('StoredProcedure')
  const [detailsData, setDetailsData] = useState([])
  const [detailsLoading, setDetailsLoading] = useState(false)

  const handleFetchDetails = (databaseName, serverName) => {
    setSelectedDatabase(databaseName)
    setSelectedServer(serverName || '')
    setIsModalVisible(true)
    setDetailsData([])
  }

  const handleGetResult = async () => {
    if (!selectedServer) {
      message.error('Server name is required')
      return
    }
    
    setDetailsLoading(true)
    try {
      const data = await databaseService.getDatabaseObjects(
        selectedServer,
        selectedDatabase,
        objectType
      )
      setDetailsData(data)
      message.success('Database objects fetched successfully')
    } catch (error) {
      console.error('Failed to fetch details:', error)
      notification.error({
        message: 'Error',
        description: error?.response?.data?.message || error?.message || 'Failed to fetch database objects',
        style: {
          background: '#fff',
          border: '2px solid #ff4d4f',
        },
        duration: 5,
      })
    } finally {
      setDetailsLoading(false)
    }
  }

  const detailColumns = [
    { 
      title: 'Name', 
      dataIndex: 'name', 
      key: 'name',
      width: 250,
    },
    { 
      title: 'Schema', 
      dataIndex: 'schema', 
      key: 'schema',
      width: 100,
    },
    { 
      title: 'Object Type', 
      dataIndex: 'objectType', 
      key: 'objectType',
      width: 150,
    },
    {
      title: 'Size (KB)',
      dataIndex: 'sizeKB',
      key: 'sizeKB',
      width: 110,
      align: 'right',
      render: (size) =>
        size != null
          ? Number(size).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : '—',
    },
    { 
      title: 'Server Name', 
      dataIndex: 'serverName', 
      key: 'serverName',
      width: 150,
    },
    { 
      title: 'Database Name', 
      dataIndex: 'databaseName', 
      key: 'databaseName',
      width: 150,
    },
    { 
      title: 'Created Date', 
      dataIndex: 'createdDate', 
      key: 'createdDate',
      width: 180,
      render: (date) => date ? dayjs(date).format('YYYY-MM-DD HH:mm:ss') : 'N/A',
    },
    { 
      title: 'Last Altered Date', 
      dataIndex: 'lastAlteredDate', 
      key: 'lastAlteredDate',
      width: 180,
      render: (date) => date ? dayjs(date).format('YYYY-MM-DD HH:mm:ss') : 'N/A',
    },
  ]

  if (isLoading) {
    return <Spin size="large" />
  }

  if (!databaseInfo || (Array.isArray(databaseInfo) && databaseInfo.length === 0)) {
    return <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>No data available</div>
  }

  const columns = [
    {
      title: 'Database Name',
      dataIndex: 'databaseName',
      key: 'databaseName',
    },
    {
      title: 'Database Size (MB)',
      dataIndex: 'databaseSize',
      key: 'databaseSize',
      render: (size) => size ? size.toFixed(2) : 'N/A',
    },
    {
      title: 'Table Count',
      dataIndex: 'tableCount',
      key: 'tableCount',
    },
    {
      title: 'SP Count',
      dataIndex: 'spCount',
      key: 'spCount',
    },
    {
      title: 'View Count',
      dataIndex: 'viewCount',
      key: 'viewCount',
    },
    {
      title: 'Synonym Count',
      dataIndex: 'synonymCount',
      key: 'synonymCount',
    },
    {
      title: 'Trigger Count',
      dataIndex: 'triggerCount',
      key: 'triggerCount',
    },
    {
      title: '',
      key: 'action',
      render: (_, record) => (
        <a 
          onClick={() => handleFetchDetails(record.databaseName, record.serverName)}
          style={{ color: '#1890ff', cursor: 'pointer' }}
        >
          Fetch Further Details
        </a>
      ),
    },
  ]

  return (
    <>
      <CommonTable
        columns={columns}
        dataSource={Array.isArray(databaseInfo) ? databaseInfo : [databaseInfo]}
        rowKey="databaseId"
        pagination={false}
      />

      <Modal
        title={
          <div style={{ 
            background: '#000', 
            color: '#fff', 
            margin: '-20px -24px 20px',
            padding: '16px 24px',
            borderBottom: '3px solid #FFE600'
          }}>
            Details
          </div>
        }
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Button onClick={() => setIsModalVisible(false)}>
              Close
            </Button>
          </div>
        }
        width={1000}
        closeIcon={<span style={{ color: '#fff' }}>×</span>}
      >
        <div style={{ display: 'flex', gap: 20, marginBottom: 20, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Database Name
            </label>
            <Input 
              value={selectedDatabase}
              readOnly
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Object Type
            </label>
            <Select
              value={objectType}
              onChange={setObjectType}
              style={{ width: '100%' }}
            >
              <Select.Option value="StoredProcedure">Stored Procedure</Select.Option>
              <Select.Option value="Table">Table</Select.Option>
              <Select.Option value="Routine">Routine</Select.Option>
              
            </Select>
          </div>
          <div>
            <Button
              style={{
                background: '#FFE600',
                color: '#000',
                border: 'none',
                fontWeight: 'bold',
                height: '32px',
                padding: '0 30px',
              }}
              onClick={handleGetResult}
              loading={detailsLoading}
            >
              Get Result
            </Button>
          </div>
        </div>

        <Table
          columns={detailColumns}
          dataSource={detailsData}
          pagination={false}
          bordered
          size="small"
          rowKey="name"
          scroll={{ x: 'max-content', y: 300 }}
        />
      </Modal>
    </>
  )
}

export default DatabaseDetails
